/*
 * A customer's PUBLIC WEBSITE — its own entry, separate from app.js.
 *
 * A club's site used to boot the whole operator app: the dashboard's
 * stylesheet (every utility of every screen, the sign-in shell, both
 * map engines' CSS), the route table's client, the step-up password
 * prompt's interceptors, the service worker that makes the dashboard
 * installable, and a lookup table naming every page in the product.
 * Its visitors are the club's members and the public, who will never
 * open any of that.
 *
 * What a site page needs is here and nothing else: Inertia, its
 * template's renderers before it renders (siteComponents.js — the
 * page was rendered on the server and is HYDRATED, so it cannot
 * render once without them), the native shells' bridges (the report
 * app shows /site pages), error reporting and stale-chunk recovery,
 * shared with app.js so the two cannot drift.
 *
 * app.blade.php picks this entry by the page COMPONENT, never by URL —
 * a vanity domain serves the same pages from `/`.
 * harness/sitebundle_verify.mjs holds the line: nothing reachable from
 * here may reach the dashboard's code.
 */
import '../css/site.css';

import { createInertiaApp, router } from '@inertiajs/vue3';
import { createApp, createSSRApp, h } from 'vue';
import { loadSiteComponents } from './Pages/Public/Site/siteComponents.js';
import { setSitePrefix } from './Pages/Public/Site/siteLocation.js';
import { recoverFromStaleBundles, reportClientErrors } from './lib/inertiaClient.js';
import { installNativeShell } from './lib/nativeShell.js';

installNativeShell();

const PAGES = import.meta.glob('./Pages/Public/Site/*.vue');

/**
 * A page that is not a website page — a form on a site that answers by
 * sending somebody into the product. This bundle cannot draw it, so
 * the browser loads it properly: Inertia has already put its URL in
 * the address bar by the time this mounts.
 */
const ElsewhereInTheProduct = {
    mounted() {
        window.location.reload();
    },
    render: () => null,
};

let theme;
function siteTheme() {
    if (theme === undefined) {
        try {
            theme = JSON.parse(document.getElementById('app')?.dataset.page || '{}')?.props?.layout?.theme || null;
        } catch {
            theme = null;
        }
    }
    return theme;
}

router.on('success', (event) => {
    setSitePrefix(event.detail?.page?.props?.site_prefix);
});

createInertiaApp({
    // SiteMeta resolves a public page's whole title; ours is not on it.
    title: (title) => title,
    resolve: (name) => {
        const page = PAGES[`./Pages/${name}.vue`];
        if (!page) return ElsewhereInTheProduct;
        // The first page's theme: every later page of a site shares it.
        return Promise.all([page(), loadSiteComponents(siteTheme())]).then(([module]) => module);
    },
    setup({ el, App, props, plugin }) {
        // go.trailsiq.com/{slug}: the prefix the server says this
        // host puts in front of the site's paths. Before mount, so the
        // links are right from the first render (siteLocation.js).
        setSitePrefix(props.initialPage?.props?.site_prefix);
        // Server-rendered and hydrated; mounted the ordinary way only
        // when the renderer was down and the page arrived empty.
        const app = (el?.hasChildNodes() ? createSSRApp : createApp)({ render: () => h(App, props) })
            .use(plugin);
        reportClientErrors(app);
        recoverFromStaleBundles(router);
        return app.mount(el);
    },
    progress: {
        color: '#4B5563',
    },
});

// No service worker on a public website. It makes the DASHBOARD
// installable, and a club's visitors are not installing it. On a
// vanity domain, take away any worker registered before that rule
// existed: a cached entry would stick to the customer's host.
const canonicalHost = document.querySelector('meta[name="tiq-canonical-host"]')?.content || '';
if (canonicalHost !== '' && window.location.hostname !== canonicalHost && 'serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations()
        .then((regs) => regs.forEach((r) => r.unregister().catch(() => {})))
        .catch(() => {});
}
